import type { ListParams, PageResult } from '@beonit/adaptive-raindrop';
import { httpClient } from '@shared/infrastructure/http/http-client.js';
import { listQuery } from '@shared/infrastructure/http/list-query.js';
import { isControlKind, isControlOwner } from '../lib/group-owner.js';
import { FLAT_FIELDS_METHOD } from '../lib/metrics-series.js';
import { labelStateOf } from '../lib/video-labelling.js';
import type {
  AddedMembers,
  AnalysisGroup,
  AnalysisRun,
  CreateGroupDraft,
  DatasetCurrent,
  DatasetLabelType,
  DatasetVersionDetail,
  DatasetVersionGestureLabels,
  DatasetVersionList,
  DatasetVersionMember,
  DatasetVersionReason,
  DatasetVersionSummary,
  DatasetVersionTruthLabels,
  ExecutionChange,
  ExecutionConfig,
  ExecutionConfigRef,
  ExecutionDataset,
  ExecutionDatasetSource,
  ExecutionMeasurement,
  ExecutionSubscription,
  GroupBatch,
  GroupBatchCancellation,
  GroupBatchDeletion,
  GroupBatchPause,
  GroupBatchResume,
  GroupComparison,
  GroupExecutionList,
  GroupExecutionQueueState,
  GroupFilters,
  GroupLatestParameters,
  GroupLaunchParameters,
  GroupMember,
  GroupMembershipRef,
  GroupMetrics,
  GroupOwner,
  GroupReanalysis,
  GroupRunResult,
  GroupSourceType,
  GroupWaitingExecution,
  MethodDelta,
  MetricsSnapshot,
  ReanalysisStatus,
  RunStatus,
  UpdateGroupDraft,
  VideoFlip,
  VideoOutcome,
} from '../model.js';
import { type RunPayload, toRun } from './analysis-runs.api.js';

interface GroupPayload {
  id: number;
  name: string;
  description: string | null;
  members_changed_at?: string | null;
  /** Absent, or null, when the group never ran — and from an API older than it. */
  latest_parameters?: LatestParametersPayload | null;
  created_at: string | null;
  updated_at: string | null;
  counts?: {
    members: number;
    runs: number;
    runs_by_status: Partial<Record<RunStatus, number>>;
    /** Absent from a Virtual Profiler older than the queue of executions. */
    executions_waiting?: number;
  };
  last_batch?: string | null;
  last_snapshot?: {
    snapshot_id: string;
    computed_at: string;
    incoherences_detected_pct: number | null;
    false_positives_pct: number | null;
    correct_pct: number | null;
    incoherences_detected_all_videos_pct?: number | null;
    false_positives_all_videos_pct?: number | null;
    /** Every method's figures; absent from an API older than them. */
    methods?: Record<string, FiguresPayload> | null;
  } | null;
  /** Absent, or null, from an API older than the executions' history. */
  reanalysis?: ReanalysisPayload | null;
}

interface ConfigRefPayload {
  id?: number | null;
  name?: string | null;
  version?: number | null;
}

/** What the latest execution that was not cancelled ran with, as upstream read it. */
export interface LatestParametersPayload {
  batch?: string | null;
  config?:
    | (ConfigRefPayload & {
        version_id?: string | null;
        engines_hash?: string | null;
      })
    | null;
  threshold_pct?: number | null;
  engines?: string[] | null;
}

/** Upstream's codes, loosely: a code this build does not know is dropped, never guessed at. */
type ChangePayload = { code?: string } & Record<string, unknown>;

export interface ReanalysisPayload {
  status?: string;
  latest_batch?: string | null;
  reasons?: ChangePayload[] | null;
  engine_versions_checked?: boolean;
}

type FiguresPayload = {
  incoherences_detected_pct?: number | null;
  false_positives_pct?: number | null;
  correct_pct?: number | null;
  /** «Métricas globales»: absent from an API older than them. */
  incoherences_detected_all_videos_pct?: number | null;
  false_positives_all_videos_pct?: number | null;
} | null;

interface OutcomePayload {
  outcome?: string;
  false_positive?: boolean;
}

interface FlipPayload {
  source_id: number;
  name?: string | null;
  label_type?: string | null;
  methods?: Record<string, { a?: OutcomePayload | null; b?: OutcomePayload | null }>;
}

interface BatchPayload {
  batch: string;
  started_at: string | null;
  finished_at: string | null;
  runs: number;
  runs_by_status: Partial<Record<RunStatus, number>>;
  /** Absent, or null, from an API that does not count videos yet. */
  members?: number | null;
  members_by_status?: Partial<Record<RunStatus, number>> | null;
  /** Absent from an API older than the automatic retries. */
  complete?: boolean;
  snapshot_id: string | null;
  /** Absent from an API older than the cancellation of executions. */
  cancelled?: boolean;
  /** This and every field below: absent from an API older than the executions' history. */
  config?: ExecutionConfigPayload | null;
  threshold_pct?: number | null;
  /** The configuration version's own threshold: absent from an API older than it. */
  config_threshold_pct?: number | null;
  engines?: string[] | null;
  /** Absent from an API older than the coherence control. */
  gesture_source?: string | null;
  engine_versions?: Record<string, string> | null;
  series_key?: string | null;
  parameters_source?: string | null;
  metrics?: {
    snapshot_id?: string | null;
    threshold_pct?: number | null;
    series_key?: string | null;
    comparable_with_previous?: boolean;
    videos_total?: number | null;
    videos_evaluated?: number | null;
    methods?: Record<string, FiguresPayload> | null;
  } | null;
  /** Its place in the group's queue of executions. Absent from an API older than the pause. */
  state?: string | null;
  /** Absent from an API older than the dataset versions. */
  dataset?: {
    version: number;
    key: string;
    videos: number;
    previous_version: number | null;
    added: DatasetSourcePayload[];
    removed: DatasetSourcePayload[];
  } | null;
}

interface DatasetSourcePayload {
  source_type: GroupSourceType;
  source_id: number;
  name: string | null;
}

/** A stored dataset version as the gateway lists it; `key` and `members` only on one read alone. */
interface DatasetVersionPayload {
  version: number;
  key?: string;
  created_at: string;
  created_by?: string | null;
  reason?: string;
  note?: string | null;
  videos?: number;
  labelled?: number;
  previous_version?: number | null;
  added?: DatasetSourcePayload[];
  removed?: DatasetSourcePayload[];
  label_types?: string[];
  members?: DatasetMemberPayload[];
}

interface DatasetMemberPayload {
  source_type: GroupSourceType;
  source_id: number;
  name: string | null;
  source_deleted?: boolean;
  labels?: {
    gestures?: DatasetVersionGestureLabels;
    gesture_alerts?: DatasetVersionGestureLabels;
    truth?: DatasetVersionTruthLabels;
  } | null;
}

interface DatasetVersionListPayload {
  items?: DatasetVersionPayload[];
  current?: Partial<DatasetCurrent> | null;
}

const DATASET_LABEL_TYPES: readonly DatasetLabelType[] = ['gestures', 'gesture_alerts', 'truth'];

const DATASET_VERSION_REASONS: readonly DatasetVersionReason[] = [
  'members',
  'labels',
  'manual',
  'execution',
  'migration',
];

function toDatasetVersionSummary(payload: DatasetVersionPayload): DatasetVersionSummary {
  return {
    version: payload.version,
    createdAt: new Date(payload.created_at),
    createdBy: payload.created_by ?? null,
    // A reason this build does not know reads as asked for: somebody, or something, did.
    reason: DATASET_VERSION_REASONS.find((reason) => reason === payload.reason) ?? 'manual',
    note: payload.note ?? null,
    videos: payload.videos ?? 0,
    labelled: payload.labelled ?? 0,
    previousVersion: payload.previous_version ?? null,
    added: (payload.added ?? []).map(toDatasetSource),
    removed: (payload.removed ?? []).map(toDatasetSource),
    labelTypes: (payload.label_types ?? []).filter((type): type is DatasetLabelType =>
      DATASET_LABEL_TYPES.includes(type as DatasetLabelType),
    ),
  };
}

function toDatasetVersionMember(payload: DatasetMemberPayload): DatasetVersionMember {
  const labels = payload.labels;

  return {
    sourceType: payload.source_type,
    sourceId: payload.source_id,
    name: payload.name ?? null,
    sourceDeleted: payload.source_deleted === true,
    labels: labels
      ? {
          ...(labels.gestures ? { gestures: { ...labels.gestures } } : {}),
          ...(labels.gesture_alerts ? { gestureAlerts: { ...labels.gesture_alerts } } : {}),
          ...(labels.truth ? { truth: { ...labels.truth } } : {}),
        }
      : null,
  };
}

function toDatasetVersionDetail(payload: DatasetVersionPayload): DatasetVersionDetail {
  return {
    ...toDatasetVersionSummary(payload),
    key: payload.key ?? '',
    members: (payload.members ?? []).map(toDatasetVersionMember),
  };
}

/** Newest first, whatever order the gateway sends: the first is «la última». */
function toDatasetVersionList(payload: DatasetVersionListPayload | null): DatasetVersionList {
  return {
    items: (payload?.items ?? [])
      .map(toDatasetVersionSummary)
      .sort((a, b) => b.version - a.version),
    current: {
      key: payload?.current?.key ?? '',
      version: payload?.current?.version ?? null,
      changed: payload?.current?.changed === true,
      pending: payload?.current?.pending ?? 0,
    },
  };
}

const EXECUTION_QUEUE_STATES: readonly GroupExecutionQueueState[] = [
  'waiting',
  'starting',
  'started',
  'paused',
];

/**
 * A state this build does not know is none: the counts still say where the execution stands. Also
 * read for a configuration's executions, which carry their group's word for it.
 */
export function toQueueState(state: string | null | undefined): GroupExecutionQueueState | null {
  return EXECUTION_QUEUE_STATES.find((known) => known === state) ?? null;
}

interface ExecutionConfigPayload {
  id?: number | null;
  name?: string | null;
  version?: number | null;
  version_id?: string | null;
  engines_hash?: string | null;
}

/** An execution launched while its group was busy: it has no runs until its turn comes. */
interface WaitingPayload {
  batch: string;
  state?: string;
  queue_position?: number | null;
  queued_at?: string | null;
  launched_by?: number | null;
  config?: ExecutionConfigPayload | null;
  threshold_pct?: number | null;
  config_threshold_pct?: number | null;
  engines?: string[] | null;
  gesture_source?: string | null;
  members?: number | null;
}

interface MemberPayload {
  member_id: number;
  /** One of the two: a group's id, or a control's kind (absent from an older gateway). */
  group_id: number | null;
  control_kind?: string | null;
  source_type: GroupMember['sourceType'];
  source_id: number;
  name: string | null;
  source_status: string | null;
  /** Upstream's word: the single label's (`incoherence`, `truthful`) or a state's. */
  label: string | null;
  pinned_run_id: string | null;
  added_at: string | null;
  resolved: {
    bucket: string;
    run_id: string | null;
    status: RunStatus | null;
    degraded: boolean;
    not_requested: boolean;
    engine_versions: Record<string, string> | null;
    engines_hash: string | null;
  };
}

interface SnapshotPayload {
  snapshot_id: string;
  group_id: number | null;
  control_kind?: string | null;
  batch: string | null;
  trigger: 'auto' | 'manual' | 'answer_key';
  computed_at: string;
  threshold_pct: number;
  incoherences_detected_pct: number | null;
  false_positives_pct: number | null;
  correct_pct: number | null;
  /** Absent from an API older than the per-method measurements. */
  methods?: Record<
    string,
    {
      incoherences_detected_pct: number | null;
      false_positives_pct: number | null;
      correct_pct: number | null;
      /** The global metrics; absent from an API older than them. */
      incoherences_detected_all_videos_pct?: number | null;
      false_positives_all_videos_pct?: number | null;
    }
  >;
  config_version?: number | null;
  videos_evaluated: number;
  videos_total: number;
  series_key: string | null;
  comparable_with_previous: boolean;
  note: string | null;
}

/**
 * Upstream's metrics document, as much of it as the screens read.
 *
 * Deliberately partial: Virtual Profiler's own shape is large and grows, and declaring all of
 * it would mean a release here every time it gains a field nobody reads.
 */
interface MetricsPayload {
  /** `batch` only when one execution was asked for, and upstream honoured it. */
  scope?: { series_key?: string | null; batch?: string | null };
  global?: {
    videos_total?: number;
    videos_labeled?: number;
    videos_evaluated?: number;
    default_threshold_pct?: number;
    methods?: Record<
      string,
      {
        incoherents_detected_pct?: number | null;
        truthful_flagged_pct?: number | null;
        correctness_pct?: number | null;
        incoherents_detected_over_all_pct?: number | null;
        false_positive_video_pct?: number | null;
      }
    >;
  };
}

interface ComparisonPayload {
  deltas?: Record<string, FiguresPayload>;
  figures?: Record<string, { a?: FiguresPayload; b?: FiguresPayload }>;
  flips?: FlipPayload[];
  same_series?: boolean;
  common_only?: boolean;
  only_in_a?: Array<{ source_id: number; name?: string | null }>;
  only_in_b?: Array<{ source_id: number; name?: string | null }>;
  common?: number;
  /** Only between two executions of one group; absent from an older API. */
  parameter_changes?: ChangePayload[] | null;
}

function date(value: string | null | undefined): Date | null {
  return value ? new Date(value) : null;
}

const REANALYSIS_STATUSES: readonly ReanalysisStatus[] = [
  'never_run',
  'running',
  'up_to_date',
  'outdated',
];

const orNull = <T>(value: T | null | undefined): T | null => value ?? null;

function configRef(payload: unknown): ExecutionConfigRef {
  const ref = (payload ?? {}) as ConfigRefPayload;

  return {
    id: orNull(ref.id),
    name: orNull(ref.name),
    version: orNull(ref.version),
  };
}

function strings(value: unknown): string[] {
  return Array.isArray(value)
    ? value.filter((item): item is string => typeof item === 'string')
    : [];
}

/**
 * One change, or null for a code this build cannot word: the screens write a sentence per code,
 * and a code nobody can word would be a blank line in a list of reasons.
 */
function toChange(payload: ChangePayload): ExecutionChange | null {
  switch (payload.code) {
    case 'config_changed':
      return {
        code: 'config_changed',
        from: configRef(payload.from),
        to: configRef(payload.to),
      };
    case 'config_version_changed':
      return {
        code: 'config_version_changed',
        configId: orNull(payload.config_id as number | null | undefined),
        from: orNull(payload.from as number | null | undefined),
        to: orNull(payload.to as number | null | undefined),
      };
    case 'threshold_changed':
      return {
        code: 'threshold_changed',
        from: orNull(payload.from as number | null | undefined),
        to: orNull(payload.to as number | null | undefined),
      };
    case 'engines_changed':
      return {
        code: 'engines_changed',
        from: strings(payload.from),
        to: strings(payload.to),
      };
    case 'engine_version_changed':
      return typeof payload.engine === 'string'
        ? {
            code: 'engine_version_changed',
            engine: payload.engine,
            from: String(payload.from ?? ''),
            to: String(payload.to ?? ''),
          }
        : null;
    // Worded without its moment when it does not come: that the videos changed is the reason.
    case 'members_changed':
      return {
        code: 'members_changed',
        changedAt: typeof payload.changed_at === 'string' ? date(payload.changed_at) : null,
      };
    default:
      return null;
  }
}

function toChanges(payload: readonly ChangePayload[] | null | undefined): ExecutionChange[] {
  return (payload ?? []).flatMap((change) => {
    const read = toChange(change ?? {});

    return read ? [read] : [];
  });
}

/** Null from an API older than it, or with a status this build does not know. */
export function toReanalysis(
  payload: ReanalysisPayload | null | undefined,
): GroupReanalysis | null {
  if (!payload || !REANALYSIS_STATUSES.includes(payload.status as ReanalysisStatus)) return null;

  return {
    status: payload.status as ReanalysisStatus,
    latestBatch: payload.latest_batch ?? null,
    reasons: toChanges(payload.reasons),
    engineVersionsChecked: payload.engine_versions_checked === true,
  };
}

function toFigures(raw: FiguresPayload | undefined): MethodDelta {
  return {
    incoherencesDetectedPct: raw?.incoherences_detected_pct ?? null,
    falsePositivesPct: raw?.false_positives_pct ?? null,
    correctPct: raw?.correct_pct ?? null,
    incoherencesDetectedAllVideosPct: raw?.incoherences_detected_all_videos_pct ?? null,
    falsePositivesAllVideosPct: raw?.false_positives_all_videos_pct ?? null,
  };
}

function toOutcome(raw: OutcomePayload | null | undefined): VideoOutcome | null {
  return raw?.outcome ? { outcome: raw.outcome, falsePositive: raw.false_positive === true } : null;
}

function toFlip(raw: FlipPayload): VideoFlip {
  return {
    sourceId: raw.source_id,
    name: raw.name ?? null,
    label: labelStateOf(raw.label_type),
    methods: Object.fromEntries(
      Object.entries(raw.methods ?? {}).map(([method, sides]) => [
        method,
        { a: toOutcome(sides?.a), b: toOutcome(sides?.b) },
      ]),
    ),
  };
}

/** A figure that did not arrive is «sin datos», never zero; a cancelled execution measured nothing. */
function toMeasurement(payload: BatchPayload): ExecutionMeasurement | null {
  const metrics = payload.metrics;
  if (payload.cancelled === true || !metrics?.snapshot_id) return null;
  if (typeof metrics.threshold_pct !== 'number') return null;

  return {
    snapshotId: metrics.snapshot_id,
    thresholdPct: metrics.threshold_pct,
    seriesKey: metrics.series_key ?? null,
    comparableWithPrevious: metrics.comparable_with_previous === true,
    videosTotal: metrics.videos_total ?? null,
    videosEvaluated: metrics.videos_evaluated ?? null,
    methods: Object.fromEntries(
      Object.entries(metrics.methods ?? {}).map(([method, raw]) => [method, toFigures(raw)]),
    ),
  };
}

function toExecutionConfig(
  config: ExecutionConfigPayload | null | undefined,
): ExecutionConfig | null {
  return config
    ? {
        id: config.id ?? null,
        name: config.name ?? null,
        version: config.version ?? null,
        versionId: config.version_id ?? null,
        enginesHash: config.engines_hash ?? null,
      }
    : null;
}

function toDatasetSource(source: DatasetSourcePayload): ExecutionDatasetSource {
  return {
    sourceType: source.source_type,
    sourceId: source.source_id,
    name: source.name ?? null,
  };
}

function toDataset(dataset: BatchPayload['dataset']): ExecutionDataset | null {
  if (!dataset) return null;

  return {
    version: dataset.version,
    key: dataset.key,
    videos: dataset.videos,
    previousVersion: dataset.previous_version ?? null,
    added: (dataset.added ?? []).map(toDatasetSource),
    removed: (dataset.removed ?? []).map(toDatasetSource),
  };
}

function toBatch(batch: BatchPayload): GroupBatch {
  return {
    batch: batch.batch,
    startedAt: date(batch.started_at),
    finishedAt: date(batch.finished_at),
    runs: batch.runs,
    runsByStatus: batch.runs_by_status ?? {},
    members: batch.members ?? null,
    membersByStatus: batch.members_by_status ?? null,
    // Before the retries, an execution was over when its last run finished.
    complete: batch.complete ?? batch.finished_at !== null,
    snapshotId: batch.snapshot_id,
    cancelled: batch.cancelled === true,
    config: toExecutionConfig(batch.config),
    thresholdPct: batch.threshold_pct ?? null,
    configThresholdPct:
      typeof batch.config_threshold_pct === 'number' ? batch.config_threshold_pct : null,
    engines: [...(batch.engines ?? [])],
    gestureSource: batch.gesture_source ?? 'engine',
    engineVersions: batch.engine_versions ? { ...batch.engine_versions } : null,
    seriesKey: batch.series_key ?? null,
    parametersSource:
      batch.parameters_source === 'recorded' || batch.parameters_source === 'runs'
        ? batch.parameters_source
        : null,
    metrics: toMeasurement(batch),
    queueState: toQueueState(batch.state),
    dataset: toDataset(batch.dataset),
  };
}

/**
 * A state this build does not know is read as waiting: whatever it is, it has not started, and
 * «Empezando» is the one that must not offer to take it out of the queue.
 */
function toWaiting(payload: WaitingPayload): GroupWaitingExecution {
  return {
    batch: payload.batch,
    state: payload.state === 'starting' ? 'starting' : 'waiting',
    queuePosition: payload.queue_position ?? 0,
    queuedAt: new Date(payload.queued_at ?? 0),
    launchedBy: payload.launched_by ?? null,
    config: toExecutionConfig(payload.config),
    thresholdPct: typeof payload.threshold_pct === 'number' ? payload.threshold_pct : null,
    configThresholdPct:
      typeof payload.config_threshold_pct === 'number' ? payload.config_threshold_pct : null,
    engines: strings(payload.engines),
    gestureSource: payload.gesture_source ?? 'engine',
    members: typeof payload.members === 'number' ? payload.members : null,
  };
}

/**
 * What the latest execution ran with, or null when the group never ran. An answer without a batch
 * names no execution, and is read as none rather than as one nobody could open.
 */
export function toLatestParameters(
  payload: LatestParametersPayload | null | undefined,
): GroupLatestParameters | null {
  if (!payload || typeof payload.batch !== 'string' || payload.batch === '') return null;

  return {
    batch: payload.batch,
    config: payload.config ? configRef(payload.config) : null,
    thresholdPct: typeof payload.threshold_pct === 'number' ? payload.threshold_pct : null,
    engines: strings(payload.engines),
  };
}

/**
 * Every method of a group's last measurement. An API older than `methods` sends only the flat
 * figures, which are `FLAT_FIELDS_METHOD`'s: they are kept under that method's name and no other.
 */
function snapshotMethods(
  snapshot: NonNullable<GroupPayload['last_snapshot']>,
): Record<string, MethodDelta> {
  if (snapshot.methods) {
    return Object.fromEntries(
      Object.entries(snapshot.methods).map(([method, raw]) => [method, toFigures(raw)]),
    );
  }

  return { [FLAT_FIELDS_METHOD]: toFigures(snapshot) };
}

function toGroup(payload: GroupPayload): AnalysisGroup {
  return {
    id: payload.id,
    name: payload.name,
    description: payload.description,
    membersChangedAt: date(payload.members_changed_at),
    latestParameters: toLatestParameters(payload.latest_parameters),
    reanalysis: toReanalysis(payload.reanalysis),
    createdAt: date(payload.created_at),
    updatedAt: date(payload.updated_at),
    members: payload.counts?.members ?? 0,
    runs: payload.counts?.runs ?? 0,
    runsByStatus: payload.counts?.runs_by_status ?? {},
    executionsWaiting: payload.counts?.executions_waiting ?? 0,
    lastBatch: payload.last_batch ?? null,
    lastMeasurement: payload.last_snapshot
      ? {
          snapshotId: payload.last_snapshot.snapshot_id,
          computedAt: new Date(payload.last_snapshot.computed_at),
          incoherencesDetectedPct: payload.last_snapshot.incoherences_detected_pct,
          falsePositivesPct: payload.last_snapshot.false_positives_pct,
          correctPct: payload.last_snapshot.correct_pct,
          incoherencesDetectedAllVideosPct:
            payload.last_snapshot.incoherences_detected_all_videos_pct ?? null,
          falsePositivesAllVideosPct: payload.last_snapshot.false_positives_all_videos_pct ?? null,
          methods: snapshotMethods(payload.last_snapshot),
        }
      : null,
  };
}

function toMember(payload: MemberPayload): GroupMember {
  return {
    memberId: payload.member_id,
    groupId: isControlKind(payload.control_kind) ? null : payload.group_id,
    controlKind: isControlKind(payload.control_kind) ? payload.control_kind : null,
    sourceType: payload.source_type,
    sourceId: payload.source_id,
    name: payload.name,
    sourceStatus: payload.source_status,
    label: labelStateOf(payload.label),
    pinnedRunId: payload.pinned_run_id,
    addedAt: date(payload.added_at),
    resolved: {
      bucket: payload.resolved.bucket,
      runId: payload.resolved.run_id,
      status: payload.resolved.status,
      degraded: payload.resolved.degraded,
      notRequested: payload.resolved.not_requested,
      engineVersions: payload.resolved.engine_versions,
      enginesHash: payload.resolved.engines_hash,
    },
  };
}

function toSnapshot(payload: SnapshotPayload): MetricsSnapshot {
  return {
    snapshotId: payload.snapshot_id,
    groupId: isControlKind(payload.control_kind) ? null : payload.group_id,
    controlKind: isControlKind(payload.control_kind) ? payload.control_kind : null,
    batch: payload.batch,
    trigger: payload.trigger,
    computedAt: new Date(payload.computed_at),
    thresholdPct: payload.threshold_pct,
    incoherencesDetectedPct: payload.incoherences_detected_pct,
    falsePositivesPct: payload.false_positives_pct,
    correctPct: payload.correct_pct,
    methods: Object.fromEntries(
      Object.entries(payload.methods ?? {}).map(([method, raw]) => [
        method,
        {
          incoherencesDetectedPct: raw.incoherences_detected_pct ?? null,
          falsePositivesPct: raw.false_positives_pct ?? null,
          correctPct: raw.correct_pct ?? null,
          incoherencesDetectedAllVideosPct: raw.incoherences_detected_all_videos_pct ?? null,
          falsePositivesAllVideosPct: raw.false_positives_all_videos_pct ?? null,
        },
      ]),
    ),
    configVersion: payload.config_version ?? null,
    videosEvaluated: payload.videos_evaluated,
    videosTotal: payload.videos_total,
    seriesKey: payload.series_key,
    comparableWithPrevious: payload.comparable_with_previous,
    note: payload.note,
  };
}

/**
 * Upstream's per-method metric names, mapped to the three the screens speak.
 *
 * `incoherents_detected` / `truthful_flagged` / `correctness` become «incoherencias detectadas», «falsas
 * alertas» and «aciertos». Translated once here rather than in the components, which is what
 * keeps a chart's axis label and a table's header from disagreeing.
 */
function toDelta(raw: {
  incoherents_detected_pct?: number | null;
  truthful_flagged_pct?: number | null;
  correctness_pct?: number | null;
  incoherents_detected_over_all_pct?: number | null;
  false_positive_video_pct?: number | null;
}): MethodDelta {
  return {
    incoherencesDetectedPct: raw.incoherents_detected_pct ?? null,
    falsePositivesPct: raw.truthful_flagged_pct ?? null,
    correctPct: raw.correctness_pct ?? null,
    incoherencesDetectedAllVideosPct: raw.incoherents_detected_over_all_pct ?? null,
    falsePositivesAllVideosPct: raw.false_positive_video_pct ?? null,
  };
}

function toMetrics(owner: GroupOwner, payload: MetricsPayload): GroupMetrics {
  const global = payload.global ?? {};

  return {
    groupId: isControlOwner(owner) ? null : owner.groupId,
    controlKind: isControlOwner(owner) ? owner.controlKind : null,
    batch: payload.scope?.batch ?? null,
    seriesKey: payload.scope?.series_key ?? null,
    thresholdPct: global.default_threshold_pct ?? 50,
    videosTotal: global.videos_total ?? 0,
    videosLabeled: global.videos_labeled ?? 0,
    videosEvaluated: global.videos_evaluated ?? 0,
    methods: Object.fromEntries(
      Object.entries(global.methods ?? {}).map(([method, raw]) => [method, toDelta(raw)]),
    ),
  };
}

function toComparison(payload: ComparisonPayload): GroupComparison {
  return {
    deltas: Object.fromEntries(
      Object.entries(payload.deltas ?? {}).map(([method, raw]) => [method, toFigures(raw)]),
    ),
    figures: Object.fromEntries(
      Object.entries(payload.figures ?? {}).map(([method, sides]) => [
        method,
        { a: toFigures(sides?.a), b: toFigures(sides?.b) },
      ]),
    ),
    flips: (payload.flips ?? []).map(toFlip),
    // Defaults to false: assuming two measurements are comparable is the mistake that turns a
    // change of engine into a reported improvement.
    sameSeries: payload.same_series === true,
    commonOnly: payload.common_only === true,
    onlyInA: (payload.only_in_a ?? []).map((ref) => ({
      sourceId: ref.source_id,
      name: ref.name ?? null,
    })),
    onlyInB: (payload.only_in_b ?? []).map((ref) => ({
      sourceId: ref.source_id,
      name: ref.name ?? null,
    })),
    common: payload.common ?? 0,
    parameterChanges: payload.parameter_changes ? toChanges(payload.parameter_changes) : null,
  };
}

/** What launching an execution of a group answers: a group's own run, and a control group's. */
export interface RunResultPayload {
  batch: string;
  accepted: Array<{
    member_id: number;
    source_id: number | string;
    run_id: string;
    position: number | null;
  }>;
  skipped: Array<{
    member_id?: number | null;
    source_id: number | string;
    reason: string;
    detail?: string | null;
  }>;
  /** Both absent from a Virtual Profiler older than the queue of executions. */
  queued?: boolean;
  queue_position?: number | null;
}

/**
 * Read once for both launches: a control group's run answers exactly what its group's would, and
 * two readers of one answer would be two chances to drift.
 */
export function toRunResult(result: RunResultPayload): GroupRunResult {
  return {
    batch: result.batch,
    accepted: (result.accepted ?? []).map((run) => ({
      memberId: run.member_id,
      // Upstream passes these through, and a source id has travelled as text before.
      sourceId: Number(run.source_id),
      runId: run.run_id,
      position: run.position ?? null,
    })),
    skipped: (result.skipped ?? []).map((skip) => ({
      memberId: skip.member_id ?? null,
      sourceId: Number(skip.source_id),
      reason: skip.reason,
      detail: skip.detail ?? null,
    })),
    queued: result.queued === true,
    queuePosition: result.queued === true ? (result.queue_position ?? null) : null,
  };
}

/**
 * Where an owner's resources are served: a group's under `/analysis-groups/:id`, a control's under
 * `/control-groups/:kind`, with the same sub-routes, bodies and answers (contract 2026-10-28).
 */
export function ownerPath(owner: GroupOwner): string {
  return isControlOwner(owner)
    ? `/virtual-profiler/control-groups/${owner.controlKind}`
    : `/virtual-profiler/analysis-groups/${owner.groupId}`;
}

/**
 * Boundary with the analysis groups — and, for everything a group and a control share (members,
 * executions, measurements, metrics), with the controls too: those take a `GroupOwner` and go to
 * the owner's routes. The groups listed, created, renamed and deleted here are the ones somebody
 * made; a control is read and run through `controlGroupsApi`.
 *
 * Nothing here erases a measurement, because no such endpoint exists: the snapshots of a group
 * are the record of whether the product improved.
 */
export const analysisGroupsApi = {
  list: async (
    params: ListParams,
    filters: GroupFilters = {},
  ): Promise<PageResult<AnalysisGroup>> => {
    const page = await httpClient.getPage<GroupPayload>(
      `/virtual-profiler/analysis-groups${listQuery({ ...params, search: params.search ?? filters.search })}`,
    );

    return { items: page.items.map(toGroup), meta: page.meta };
  },

  get: async (groupId: number): Promise<AnalysisGroup> =>
    toGroup(await httpClient.get<GroupPayload>(`/virtual-profiler/analysis-groups/${groupId}`)),

  /**
   * Creates the group and its members in one call, so nothing half-made is left behind. A group is
   * its videos: what they are analysed with is chosen at each launch (`run`).
   */
  create: async (draft: CreateGroupDraft): Promise<AnalysisGroup> =>
    toGroup(
      await httpClient.post<GroupPayload>('/virtual-profiler/analysis-groups', {
        name: draft.name,
        description: draft.description,
        videoIds: draft.videoIds,
      }),
    ),

  /** Renames it or rewrites what it is for. Nothing it measures lives on it, so nothing waits. */
  update: async (groupId: number, draft: UpdateGroupDraft): Promise<AnalysisGroup> =>
    toGroup(
      await httpClient.put<GroupPayload>(`/virtual-profiler/analysis-groups/${groupId}`, {
        name: draft.name,
        description: draft.description,
      }),
    ),

  remove: (groupId: number): Promise<void> =>
    httpClient.delete<void>(`/virtual-profiler/analysis-groups/${groupId}`),

  /**
   * The members, each resolved to the run it is read by.
   *
   * With a `batch`, by its latest run in that execution and nothing else — the reading the metrics
   * of that execution count with. Without one, upstream's older reading across executions.
   */
  members: async (
    owner: GroupOwner,
    scope: { batch?: string | null } = {},
  ): Promise<GroupMember[]> => {
    const answer = await httpClient.get<{ items: MemberPayload[] }>(
      `${ownerPath(owner)}/members${scope.batch ? `?batch=${encodeURIComponent(scope.batch)}` : ''}`,
    );

    return (answer.items ?? []).map(toMember);
  },

  /**
   * Adds videos to a group.
   *
   * `items` are every video asked for that is a member now, the ones that already were included,
   * so the count to report is `added`; `missing` is how many do not exist any more.
   *
   * A dataset version is stored after the add unless `deferDatasetVersion` (product owner,
   * 2026-10-29): the upload queue adds one video per call and closes the whole bulk with one
   * `createDatasetVersion(owner, { reason: 'members' })` — one version per upload, not per video.
   */
  addMembers: async (
    owner: GroupOwner,
    videoIds: readonly number[],
    options: { deferDatasetVersion?: boolean } = {},
  ): Promise<AddedMembers> => {
    const answer = await httpClient.post<{
      items?: MemberPayload[];
      added?: number;
      missing?: unknown[];
    }>(`${ownerPath(owner)}/members`, {
      videoIds: [...videoIds],
      ...(options.deferDatasetVersion ? { deferDatasetVersion: true } : {}),
    });
    const items = (answer?.items ?? []).map(toMember);

    return {
      items,
      added: answer?.added ?? items.length,
      missing: answer?.missing?.length ?? 0,
    };
  },

  removeMember: (owner: GroupOwner, memberId: number): Promise<void> =>
    httpClient.delete<void>(`${ownerPath(owner)}/members/${memberId}`),

  /**
   * Several members out in one call (2026-10-29): one dataset version for all of them. `missing`
   * were not in the group any more.
   */
  removeMembers: async (
    owner: GroupOwner,
    memberIds: readonly number[],
  ): Promise<{ removed: number[]; missing: number[] }> => {
    const answer = await httpClient.post<{ removed?: number[]; missing?: number[] }>(
      `${ownerPath(owner)}/members/remove`,
      { memberIds },
    );

    return { removed: answer?.removed ?? [], missing: answer?.missing ?? [] };
  },

  /** `runId: null` unpins. A pinned member keeps its result through later re-analyses. */
  pinMember: async (
    owner: GroupOwner,
    memberId: number,
    runId: string | null,
  ): Promise<GroupMember> =>
    toMember(
      await httpClient.put<MemberPayload>(`${ownerPath(owner)}/members/${memberId}/pin`, { runId }),
    ),

  /**
   * Launches a new execution of the whole group with the parameters chosen for it: configuration
   * (and a stored version of it, or its current one when none is named), threshold (null: the
   * configuration's own) and engines. The execution records exactly these.
   *
   * A busy group never refuses it: the execution waits its turn (`queued`, with its place) and
   * starts when the one in flight has finished, so it has no runs yet. A group run never promotes,
   * so nothing it produces becomes what a video shows until somebody promotes it. The skip reasons
   * travel verbatim, with upstream's detail when it sent one.
   */
  run: async (groupId: number, parameters: GroupLaunchParameters): Promise<GroupRunResult> =>
    toRunResult(
      await httpClient.post<RunResultPayload>(`/virtual-profiler/analysis-groups/${groupId}/run`, {
        analysisConfigId: parameters.analysisConfigId,
        // Left out rather than sent empty: absent is «its current version».
        ...(parameters.configVersionId ? { configVersionId: parameters.configVersionId } : {}),
        engines: [...parameters.engines],
        thresholdPct: parameters.thresholdPct,
        // The engine is upstream's default: only a launch fed by the labels says where they come
        // from.
        ...(parameters.gestureSource === 'human_labels' ? { gestureSource: 'human_labels' } : {}),
      }),
    ),

  /**
   * Every execution of the group, with what it ran with and what it scored — and, apart, the ones
   * waiting their turn, next first. Apart because a waiting execution has no runs: nothing that
   * reads the history may take it for one that ran, or for the latest.
   */
  batches: async (owner: GroupOwner): Promise<GroupExecutionList> => {
    const answer = await httpClient.get<{
      items: BatchPayload[];
      waiting?: WaitingPayload[];
    }>(`${ownerPath(owner)}/batches`);

    return {
      items: (answer.items ?? []).map(toBatch),
      waiting: (answer.waiting ?? [])
        .map(toWaiting)
        .sort((a, b) => a.queuePosition - b.queuePosition),
    };
  },

  /**
   * Cancels one execution of a group, or takes it out of the queue: the same call.
   *
   * Upstream withdraws the runs that had not started, asks the running ones to stop and discards
   * what they had written. A finished run is never touched — the history is immutable — and the
   * execution stays in it, marked as cancelled. An execution still waiting its turn ran nothing: it
   * just leaves the queue (`dequeued`) and the history never lists it. Calling it twice is
   * harmless: the second answer has everything `untouched` or still `cancelling`. The label travels
   * in the body because it carries an «@» and spaces.
   */
  cancelBatch: async (owner: GroupOwner, batch: string): Promise<GroupBatchCancellation> => {
    const answer = await httpClient.post<Partial<{
      batch: string;
      withdrawn: string[];
      cancelling: string[];
      finishing: string[];
      untouched: string[];
      dequeued: boolean;
    }> | null>(`${ownerPath(owner)}/batches/cancel`, { batch });

    return {
      batch: answer?.batch ?? batch,
      withdrawn: answer?.withdrawn ?? [],
      cancelling: answer?.cancelling ?? [],
      finishing: answer?.finishing ?? [],
      untouched: answer?.untouched ?? [],
      dequeued: answer?.dequeued === true,
    };
  },

  /**
   * Pauses one execution of a group (product owner, 2026-09-29): its runs that had not started
   * wait, paused, and the ones already running finish. It never holds the group — the next
   * execution waiting its turn starts — and asking twice is harmless. The label goes in the body,
   * as on the cancel.
   */
  pauseBatch: async (owner: GroupOwner, batch: string): Promise<GroupBatchPause> => {
    const answer = await httpClient.post<Partial<{
      batch: string;
      paused: number;
      still_running: number;
    }> | null>(`${ownerPath(owner)}/batches/pause`, { batch });

    return {
      batch: answer?.batch ?? batch,
      paused: answer?.paused ?? 0,
      stillRunning: answer?.still_running ?? 0,
    };
  },

  /**
   * Resumes a paused execution: it goes back to the end of the group's queue of executions, and its
   * paused runs are queued again when its turn comes — at once when the group is idle.
   */
  resumeBatch: async (owner: GroupOwner, batch: string): Promise<GroupBatchResume> => {
    const answer = await httpClient.post<Partial<{
      batch: string;
      state: string;
    }> | null>(`${ownerPath(owner)}/batches/resume`, { batch });

    return {
      batch: answer?.batch ?? batch,
      // Whatever it is when it is not waiting, its turn came: its runs are queued, or being queued.
      state: answer?.state === 'waiting' ? 'waiting' : 'started',
    };
  },

  /**
   * Deletes one execution of a group with everything it produced (product owner, 2026-09-29): its
   * runs and their results, its measurement and its figures. Refused while anything of it is in
   * flight; its paused runs go with it. A POST with the label in the body, as the other actions
   * on an execution: the label carries an «@» and spaces.
   */
  deleteBatch: async (owner: GroupOwner, batch: string): Promise<GroupBatchDeletion> => {
    const answer = await httpClient.post<Partial<{
      batch: string;
      deleted_runs: number;
    }> | null>(`${ownerPath(owner)}/batches/delete`, { batch });

    return {
      batch: answer?.batch ?? batch,
      deletedRuns: answer?.deleted_runs ?? 0,
    };
  },

  /**
   * Asks to be emailed when one execution finishes, with the page the email links to.
   *
   * The address is the session's: the gateway sends the operator's, never one from here. The label
   * travels in the body, as on the cancel. Asking twice is harmless.
   */
  subscribeToExecution: async (
    owner: GroupOwner,
    batch: string,
    resultUrl: string,
  ): Promise<ExecutionSubscription> => {
    const answer = await httpClient.post<Partial<{
      group_id: number | null;
      control_kind: string | null;
      batch: string;
      email: string;
      subscriptions: Array<{
        event: string;
        status: string;
        sent_at: string | null;
      }>;
    }> | null>(`${ownerPath(owner)}/batches/subscriptions`, {
      batch,
      resultUrl,
    });
    const finished = answer?.subscriptions?.find((entry) => entry.event === 'execution_finished');

    return {
      // The owner asked for, whatever the answer spells: it is the one the email is about.
      groupId: isControlOwner(owner) ? null : owner.groupId,
      controlKind: isControlOwner(owner) ? owner.controlKind : null,
      batch: answer?.batch ?? batch,
      email: answer?.email ?? '',
      status: finished?.status ?? 'pending',
      sentAt: date(finished?.sent_at),
    };
  },

  /**
   * The runs of one execution of a group, a page at a time, read exactly as the run history reads
   * them — the video's name included.
   */
  batchRuns: async (
    owner: GroupOwner,
    batch: string,
    params: ListParams = { page: 1, pageSize: 200 },
    filters: { status?: readonly RunStatus[] } = {},
  ): Promise<PageResult<AnalysisRun>> => {
    const page = await httpClient.getPage<RunPayload>(
      `${ownerPath(owner)}/runs${listQuery(params, {
        batch,
        status: filters.status ? [...filters.status] : undefined,
      })}`,
    );

    return { items: page.items.map(toRun), meta: page.meta };
  },

  /**
   * Which groups — and controls — these videos belong to.
   *
   * One request for the whole page. Every id asked about comes back with a key, an empty array
   * included: a missing key and «no groups» would otherwise be indistinguishable.
   */
  membership: async (videoIds: readonly number[]): Promise<Map<number, GroupMembershipRef[]>> => {
    if (videoIds.length === 0) return new Map();

    const answer = await httpClient.get<
      Record<
        string,
        Array<{
          group_id: number | null;
          control_kind?: string | null;
          name: string;
          pinned_run_id: string | null;
        }>
      >
    >(
      `/virtual-profiler/analysis-groups/membership?sourceType=manual_analysis&sourceIds=${videoIds.join(',')}`,
    );

    return new Map(
      Object.entries(answer ?? {}).map(([sourceId, memberships]) => [
        Number(sourceId),
        memberships.map((membership) => ({
          groupId: isControlKind(membership.control_kind) ? null : membership.group_id,
          controlKind: isControlKind(membership.control_kind) ? membership.control_kind : null,
          name: membership.name,
          pinnedRunId: membership.pinned_run_id,
        })),
      ]),
    );
  },

  /**
   * The metrics of a group; of one execution when `batch` is given.
   *
   * An execution the group never ran answers `VP_GROUP_BATCH_NOT_FOUND`.
   */
  metrics: async (
    owner: GroupOwner,
    query: { threshold?: number | null; batch?: string | null } = {},
  ): Promise<GroupMetrics> => {
    const params = new URLSearchParams();
    if (query.threshold !== undefined && query.threshold !== null) {
      params.set('threshold', String(query.threshold));
    }
    if (query.batch) params.set('batch', query.batch);

    return toMetrics(
      owner,
      await httpClient.get<MetricsPayload>(
        `${ownerPath(owner)}/metrics${params.size > 0 ? `?${params}` : ''}`,
      ),
    );
  },

  snapshots: async (owner: GroupOwner): Promise<MetricsSnapshot[]> => {
    const answer = await httpClient.get<{ items: SnapshotPayload[] }>(
      `${ownerPath(owner)}/snapshots`,
    );

    return (answer.items ?? []).map(toSnapshot);
  },

  compare: async (a: number, b: number, commonOnly = true): Promise<GroupComparison> =>
    toComparison(
      await httpClient.get<ComparisonPayload>(
        `/virtual-profiler/analysis-groups/compare?a=${a}&b=${b}&commonOnly=${commonOnly}`,
      ),
    ),

  /**
   * Two executions of the same group: the comparison that answers «hemos mejorado», with which
   * parameters changed between them.
   */
  compareBatches: async (owner: GroupOwner, a: string, b: string): Promise<GroupComparison> =>
    toComparison(
      await httpClient.get<ComparisonPayload>(
        `${ownerPath(owner)}/compare-batches?a=${encodeURIComponent(a)}&b=${encodeURIComponent(b)}`,
      ),
    ),

  /**
   * The stored versions of the owner's dataset, newest first, and where the dataset stands now
   * (product owner, 2026-10-29): whether it moved since the latest version and how many of its
   * videos are still to label.
   */
  datasetVersions: async (owner: GroupOwner): Promise<DatasetVersionList> =>
    toDatasetVersionList(
      await httpClient.get<DatasetVersionListPayload | null>(
        `${ownerPath(owner)}/dataset-versions`,
      ),
    ),

  /** One stored version with its videos and the labels it pinned. `VP_DATASET_VERSION_NOT_FOUND` when none. */
  datasetVersion: async (owner: GroupOwner, version: number): Promise<DatasetVersionDetail> =>
    toDatasetVersionDetail(
      await httpClient.get<DatasetVersionPayload>(
        `${ownerPath(owner)}/dataset-versions/${version}`,
      ),
    ),

  /**
   * Stores the dataset as it is now as the next version. `VP_DATASET_UNCHANGED` when it is the
   * latest already — somebody, or a launch, versioned it meanwhile.
   *
   * `reason` is `manual` (the button) unless the call closes a bulk added with
   * `deferDatasetVersion` (`members`, 2026-10-29); only a reason other than the default travels.
   */
  createDatasetVersion: async (
    owner: GroupOwner,
    options: { note?: string | null; reason?: 'manual' | 'members' } = {},
  ): Promise<DatasetVersionDetail> =>
    toDatasetVersionDetail(
      await httpClient.post<DatasetVersionPayload>(`${ownerPath(owner)}/dataset-versions`, {
        ...(options.note ? { note: options.note } : {}),
        ...(options.reason && options.reason !== 'manual' ? { reason: options.reason } : {}),
      }),
    ),
};
